// Usage, plan and sending pause API (HXSMSF-140, HXSMSF-141).
// Function names and return shapes match the prototype the screens were built
// on, so the screens did not change when the mock data was replaced.
import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

const TIMEZONE = "Asia/Taipei";

// The backend uses ACTIVE / PAUSED and PAUSED / RESUMED; the screens and text
// keys use lower case. Converted here, in one place.
const toSending = ({ sendingStatus, lastAction }) => ({
  sendingStatus: (sendingStatus || "ACTIVE").toLowerCase(),
  lastAction: lastAction
    ? { ...lastAction, type: lastAction.type.toLowerCase() }
    : null,
});

const withSending = (row) => ({ ...row, ...toSending(row) });

// Current month in Taiwan time, e.g. "2026-10".
export const getCurrentMonth = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type).value;
  return `${get("year")}-${get("month")}`;
};

// The plan record that applies to a month: the latest effective month on or
// before it. Two records for the same month: the one saved last wins.
// Months before the first record have no plan (quota "Not set").
export const planForMonth = (history, month) =>
  [...(history || [])]
    .filter((item) => item.effectiveMonth <= month)
    .sort((a, b) =>
      a.effectiveMonth === b.effectiveMonth
        ? b.updatedAt.localeCompare(a.updatedAt)
        : b.effectiveMonth.localeCompare(a.effectiveMonth)
    )[0] || null;

// GET /admin/usage?month=YYYY-MM (SUPER_ADMIN, ADMIN)
// { month, timezone, earliestMonth, tenants: [...] }
export const getAdminUsage = async (month) => {
  const res = await api.get("/admin/usage", month ? { month } : null);
  return { ...res.data, tenants: res.data.tenants.map(withSending) };
};

// GET /admin/tenants/:id/usage (SUPER_ADMIN, ADMIN)
// This month plus the months before it (6 in all), for the tenant details page.
export const getTenantUsage = async (tenantId) => {
  const res = await api.get(`/admin/tenants/${tenantId}/usage`);
  return { months: res.data.months, ...toSending(res.data) };
};

// Plans are synced from Airtable in phase 2 (HXSMSF-138). Until then no tenant
// has a plan and the screens show "Not set".
export const getTenantPlan = async () => null;

// GET /api/usage (TENANT_ADMIN, TENANT_USER): the caller's tenant only.
// No unit price or contract start date.
export const getMyPlanUsage = async () => {
  const res = await api.get("/api/usage");
  return res.data;
};

// The caller's sending status, for the paused banner. From GET /api/me.
export const getMySendingStatus = async () => {
  const res = await api.get("/api/me", null, { silent: true });
  return toSending(res.data.tenant);
};

const listeners = new Set();

// Screens that show the sending status reload when it changes.
export const subscribeSendingChanges = (listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// POST /admin/tenants/:id/sending  body: { action: "pause" | "resume" }
export const setTenantSending = async (tenantId, action) => {
  const res = await api.create(`/admin/tenants/${tenantId}/sending`, {
    action,
  });
  listeners.forEach((listener) => listener());
  return toSending(res.data);
};
